import api from '@/api/axiosConfig.js';

const PREFIX = '/api/print-agent';
const MAX_PDF_BYTES = 10 * 1024 * 1024;
const IDEMPOTENCY_KEY = /^[A-Za-z0-9_-]{16,128}$/;
const ENROLLMENT_CODE = /^[A-Za-z0-9_-]{43}$/;
const TENANT_HOST = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;

function positiveId(value) {
  if (!((typeof value === 'number' && Number.isSafeInteger(value)) || (typeof value === 'string' && /^\d+$/.test(value)))) {
    throw new TypeError('A positive print-agent ID is required.');
  }
  const number = Number(value);
  if (!Number.isSafeInteger(number) || number <= 0) throw new TypeError('A positive print-agent ID is required.');
  return number;
}

function targetHeaders({ deviceId, printerName, idempotencyKey }) {
  const selectedDevice = positiveId(deviceId);
  if (typeof printerName !== 'string' || !printerName.trim() || printerName.length > 120 || /[\r\n]/.test(printerName)) {
    throw new TypeError('An explicitly selected printer is required.');
  }
  if (typeof idempotencyKey !== 'string' || !IDEMPOTENCY_KEY.test(idempotencyKey)) {
    throw new TypeError('An idempotency key is required for each print request.');
  }
  return {
    'X-Print-Agent-Device-Id': String(selectedDevice),
    'X-Print-Agent-Printer-Name': printerName,
    'Idempotency-Key': idempotencyKey,
  };
}

function responseData(response) {
  if (response?.data?.success !== true || response.data.data == null) {
    throw new Error('The print-agent response was invalid.');
  }
  return response.data.data;
}

/**
 * Issue a short-lived code only for the current authenticated tenant session; never store it in browser storage.
 * The agent is pasted "tenant-host/code" so one installer can pair with any Sinamon tenant.
 */
export async function createPrintAgentEnrollment() {
  const data = responseData(await api.post(`${PREFIX}/enrollments`));
  if (typeof data.code !== 'string' || !ENROLLMENT_CODE.test(data.code)
    || !Number.isInteger(data.expiresInMinutes) || data.expiresInMinutes < 1 || data.expiresInMinutes > 10
    || typeof data.tenantHost !== 'string' || !TENANT_HOST.test(data.tenantHost)) {
    throw new Error('The print-agent response was invalid.');
  }
  return {
    code: data.code,
    expiresInMinutes: data.expiresInMinutes,
    tenantHost: data.tenantHost,
    pairingKey: `${data.tenantHost}/${data.code}`,
  };
}

/** Read only devices visible to the authenticated account; no workstation is inferred from the browser. */
export async function listPrintAgentWorkstations() {
  return responseData(await api.get(`${PREFIX}/devices`));
}

export async function getPrintAgentInventory(deviceId) {
  return responseData(await api.get(`${PREFIX}/devices/${positiveId(deviceId)}/printers`));
}

export async function getPrintAgentPreferences(deviceId) {
  return responseData(await api.get(`${PREFIX}/devices/${positiveId(deviceId)}/preferences`));
}

/** Replace only the selected account's preferences for the selected workstation. */
export async function savePrintAgentPreferences(deviceId, preferences) {
  const selectedDevice = positiveId(deviceId);
  if (!preferences || typeof preferences !== 'object' || Array.isArray(preferences)
    || Object.keys(preferences).length !== 3
    || !Object.hasOwn(preferences, 'guidePrinter') || !Object.hasOwn(preferences, 'invoicePrinter')
    || !Object.hasOwn(preferences, 'labelPrinter')
    || [preferences.guidePrinter, preferences.labelPrinter].some((name) =>
      typeof name !== 'string' || name.length > 120 || /[\p{Cc}]/u.test(name)
      || (name.length > 0 && !name.trim()))) {
    throw new TypeError('Guide and label printer names must be valid.');
  }
  if (preferences.invoicePrinter !== null && (typeof preferences.invoicePrinter !== 'string'
    || preferences.invoicePrinter.length > 120 || /[\p{Cc}]/u.test(preferences.invoicePrinter)
    || (preferences.invoicePrinter.length > 0 && !preferences.invoicePrinter.trim()))) {
    throw new TypeError('The invoice printer must be valid or inherit the guide printer.');
  }
  return responseData(await api.put(`${PREFIX}/devices/${selectedDevice}/preferences`, {
    guidePrinter: preferences.guidePrinter,
    invoicePrinter: preferences.invoicePrinter,
    labelPrinter: preferences.labelPrinter,
  }));
}

/** This submits to a queue, not to physical paper. The caller owns the idempotency key and explicit target. */
export async function enqueuePrintAgentPdf({ pdf, ...target }) {
  const headers = targetHeaders(target);
  if (!(pdf instanceof Uint8Array) || pdf.byteLength < 5 || pdf.byteLength > MAX_PDF_BYTES
    || pdf[0] !== 37 || pdf[1] !== 80 || pdf[2] !== 68 || pdf[3] !== 70 || pdf[4] !== 45) {
    throw new TypeError('A bounded PDF byte payload is required.');
  }
  return responseData(await api.post(`${PREFIX}/jobs`, pdf, {
    headers: { 'Content-Type': 'application/pdf', ...headers },
  }));
}

/** The server reloads previously emitted label bytes by ID; never upload client ZPL. */
export async function enqueuePrintAgentLabel({ labelId, ...target }) {
  if (Object.keys(target).some((key) => !['deviceId', 'printerName', 'idempotencyKey'].includes(key))) {
    throw new TypeError('Client-provided label content is not accepted.');
  }
  const headers = targetHeaders(target);
  return responseData(await api.post(`${PREFIX}/jobs/labels/${positiveId(labelId)}`, undefined, { headers }));
}

/** A queued or spooler-accepted result does not confirm physical delivery. */
export async function getPrintAgentJobStatus(jobId) {
  return responseData(await api.get(`${PREFIX}/jobs/${positiveId(jobId)}`));
}
